import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  Flex,
  PageHeader,
  VisuallyHidden,
} from '@/components/ui';
import { ROUTES } from '@/config/routes';
import { useNavigate } from 'react-router-dom';
import { IconArrowLeft, IconList, IconTrash } from '@tabler/icons-react';
import { ActiveWhitelistCard } from './ActiveWhitelistCard';
import { WhitelistUploadCard } from './WhitelistUploadCard';
import { WhitelistVersionsTable } from './WhitelistVersionsTable';
import { AllWhitelistsModal } from './AllWhitelistsModal';
import { WhitelistFile, WhitelistManagementPageProps } from './types';
import {
  useCurrentWhitelist,
  useDeleteCurrentWhitelist,
  useDownloadWhitelistFile,
  useUploadOrUpdateWhitelist,
  useWhitelistVersions,
} from '@/services/singleWhitelistHooks';
import { useDeleteWhitelist, useWhitelists } from '@/services/whitelistsHooks';
import {
  CurrentWhitelist,
  transformCurrentWhitelistFromApi,
  transformWhitelistVersionsFromApi,
  WhitelistVersion,
} from '@/utils/singleWhitelistTransformers';
import { transformWhitelistsFromApi } from '@/utils/whitelistTransformers';
import {
  getWhitelistDownloadEndpoint,
  getWhitelistVersionDownloadEndpoint,
} from '@/utils/downloadEndpoints';

export const NewWhitelistManagementPage: React.FC<
  WhitelistManagementPageProps
> = ({ className }) => {
  const navigate = useNavigate();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showUploadConfirmation, setShowUploadConfirmation] =
    useState<File | null>(null);
  const [showAllWhitelists, setShowAllWhitelists] = useState(false);

  // API hooks
  const {
    data: currentWhitelistData,
    refetch: refetchCurrent,
  } = useCurrentWhitelist();
  const whitelistId = currentWhitelistData?.id;
  const { data: versionsData, isLoading: isLoadingVersions } =
    useWhitelistVersions(whitelistId, !!whitelistId);
  const uploadOrUpdateMutation = useUploadOrUpdateWhitelist();
  const deleteMutation = useDeleteCurrentWhitelist();
  const downloadMutation = useDownloadWhitelistFile();

  // For all whitelists modal
  const {
    data: allWhitelistsData,
    isLoading: isLoadingAll,
    refetch: refetchAllWhitelists,
  } = useWhitelists(undefined, { enabled: showAllWhitelists });
  const deleteAnyWhitelistMutation = useDeleteWhitelist();

  // Transform API data
  const currentWhitelist: CurrentWhitelist | null = useMemo(() => {
    if (!currentWhitelistData) return null;
    return transformCurrentWhitelistFromApi(currentWhitelistData);
  }, [currentWhitelistData]);

  const versions: WhitelistVersion[] = useMemo(() => {
    if (!versionsData?.data) return [];
    return transformWhitelistVersionsFromApi(versionsData.data);
  }, [versionsData?.data]);

  // Transform all whitelists for modal
  const allWhitelists: WhitelistFile[] = useMemo(() => {
    if (!allWhitelistsData?.data) return [];
    return transformWhitelistsFromApi(allWhitelistsData.data);
  }, [allWhitelistsData?.data]);

  const performUpload = useCallback(
    async (file: File) => {
      try {
        await uploadOrUpdateMutation.mutateAsync({
          file,
          existingWhitelistId: currentWhitelist?.id,
        });
        // Refetch data after successful upload
        await Promise.all([refetchCurrent(), refetchAllWhitelists()]);
      } catch {
        // Intentionally ignore: react-query mutation state/toasts handle user-facing errors.
      }
    },
    [
      uploadOrUpdateMutation,
      currentWhitelist?.id,
      refetchCurrent,
      refetchAllWhitelists,
    ]
  );

  // Handle file upload with confirmation
  const handleFileUpload = useCallback(
    (file: File) => {
      if (currentWhitelist) {
        // Show confirmation for update
        setShowUploadConfirmation(file);
      } else {
        // Direct upload for new whitelist
        performUpload(file);
      }
    },
    [currentWhitelist, performUpload]
  );

  const handleUploadConfirmation = useCallback(
    async (confirmed: boolean) => {
      if (confirmed && showUploadConfirmation) {
        await performUpload(showUploadConfirmation);
      }
      setShowUploadConfirmation(null);
    },
    [showUploadConfirmation, performUpload]
  );

  // Handle file download
  const handleDownloadWhitelist = useCallback(
    (file: CurrentWhitelist | WhitelistVersion) => {
      // Check if it's a CurrentWhitelist (has userId field)
      if ('userId' in file) {
        // Download current whitelist using latest version
        downloadMutation.mutate({
          apiEndpoint: getWhitelistDownloadEndpoint(file),
          filename: file.filename,
        });
      } else {
        // Download whitelist version - use current whitelist ID
        if (currentWhitelist) {
          downloadMutation.mutate({
            apiEndpoint: getWhitelistVersionDownloadEndpoint(
              currentWhitelist.id,
              file.id
            ),
            filename: file.filename,
          });
        }
      }
    },
    [downloadMutation, currentWhitelist]
  );

  // Handle delete whitelist
  const handleDeleteWhitelist = useCallback(async () => {
    if (!currentWhitelist) return;

    try {
      await deleteMutation.mutateAsync(currentWhitelist.id);
      setShowDeleteConfirmation(false);
    } catch {
      // Intentionally ignore: react-query mutation state/toasts handle user-facing errors.
    }
  }, [currentWhitelist, deleteMutation]);

  // Handle delete any whitelist from modal
  const handleDeleteAnyWhitelist = useCallback(
    async (whitelistId: number) => {
      try {
        await deleteAnyWhitelistMutation.mutateAsync(whitelistId);
        // Refresh both current and all whitelists data
        await Promise.all([refetchCurrent(), refetchAllWhitelists()]);
      } catch {
        // Intentionally ignore: react-query mutation state/toasts handle user-facing errors.
      }
    },
    [deleteAnyWhitelistMutation, refetchCurrent, refetchAllWhitelists]
  );

  // Handle opening all whitelists modal with refresh
  const handleOpenAllWhitelists = useCallback(() => {
    setShowAllWhitelists(true);
    // Refresh data when opening modal
    refetchAllWhitelists();
  }, [refetchAllWhitelists]);

  const handleBackToStudyFiles = useCallback(() => {
    navigate(ROUTES.studyFiles);
  }, [navigate]);

  // Convert current whitelist to WhitelistFile format for ActiveWhitelistCard
  const activeWhitelistForCard = useMemo(() => {
    if (!currentWhitelist) return null;

    return {
      id: currentWhitelist.id,
      filename: currentWhitelist.filename,
      uploadedBy: currentWhitelist.uploadedBy,
      uploadDate: currentWhitelist.uploadDate,
      isActive: true,
      size: currentWhitelist.size,
      contentType: currentWhitelist.contentType,
      fileUrl: currentWhitelist.fileUrl,
      userId: currentWhitelist.userId,
      lastVersionId: currentWhitelist.lastVersionId,
    };
  }, [currentWhitelist]);

  const isUploading = uploadOrUpdateMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  return (
    <Flex
      direction="column"
      style={{ height: 'inherit' }}
      className={`whitelist-management ${className || ''}`}
    >
      {/* Header */}
      <PageHeader
        title="Whitelist Management"
        description="Manage the single whitelist file that defines upload access rules"
        startSlot={
          <VisuallyHidden>
            <Button
              variant="surface"
              size="small"
              onClick={handleBackToStudyFiles}
              startIcon={<IconArrowLeft />}
            >
              Back to Study Files
            </Button>
          </VisuallyHidden>
        }
        endSlot={
          <Flex gap="2">
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenAllWhitelists}
              startIcon={<IconList />}
            >
              View All Whitelists
            </Button>
            {currentWhitelist && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => setShowDeleteConfirmation(true)}
                startIcon={<IconTrash />}
                disabled={isDeleting}
              >
                Remove all Whitelists
              </Button>
            )}
          </Flex>
        }
      />

      <Flex gap="4" flexGrow="1">
        <Flex gap="4" width="480px" direction="column">
          {activeWhitelistForCard && (
            <ActiveWhitelistCard
              activeWhitelist={activeWhitelistForCard}
              onDownload={handleDownloadWhitelist}
            />
          )}

          <WhitelistUploadCard
            onFileUpload={handleFileUpload}
            isUploading={isUploading}
          />
        </Flex>

        <Flex gap="2" flexGrow="1">
          <WhitelistVersionsTable
            versions={versions}
            onDownload={handleDownloadWhitelist}
            loading={isLoadingVersions}
            currentWhitelistId={currentWhitelist?.id}
          />
        </Flex>
      </Flex>

      {/* Upload Confirmation Dialog */}
      <Dialog.Root
        open={!!showUploadConfirmation}
        onOpenChange={() => setShowUploadConfirmation(null)}
      >
        <Dialog.Content>
          <Dialog.Body>
            <Dialog.Title>Update Whitelist</Dialog.Title>
            <Dialog.Description>
              This will update the current whitelist file. The previous version
              will be saved in the history.
            </Dialog.Description>

            <Flex gap="2" justify="end" mt="4">
              <Button
                variant="outlined"
                onClick={() => handleUploadConfirmation(false)}
              >
                Cancel
              </Button>
              <Button
                variant="classic"
                onClick={() => handleUploadConfirmation(true)}
                loading={isUploading}
              >
                Update Whitelist
              </Button>
            </Flex>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Root>

      {/* Delete Confirmation Dialog */}
      <Dialog.Root
        open={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}
      >
        <Dialog.Content>
          <Dialog.Body>
            <Dialog.Title>Delete All Whitelists</Dialog.Title>
            <Dialog.Description>
              This will permanently delete the current whitelist and all other
              whitelists with version history. This action cannot be undone.
            </Dialog.Description>
          </Dialog.Body>

          <Dialog.Footer>
            <Button
              variant="outlined"
              onClick={() => setShowDeleteConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="classic"
              color="error"
              onClick={handleDeleteWhitelist}
              loading={isDeleting}
            >
              Delete ALL Whitelists
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>

      {/* All Whitelists Modal */}
      <AllWhitelistsModal
        open={showAllWhitelists}
        onOpenChange={setShowAllWhitelists}
        whitelists={allWhitelists}
        onDownload={handleDownloadWhitelist}
        onDelete={handleDeleteAnyWhitelist}
        loading={isLoadingAll}
        isDeleting={deleteAnyWhitelistMutation.isPending}
      />
    </Flex>
  );
};
